import type { Locale } from 'date-fns'
import type { OrganisationSimple } from '@/types/organisation.model'
import type { User } from '@/types/user.model'
import { CognitoUserSession } from 'amazon-cognito-identity-js'
import { acceptHMRUpdate, defineStore, getActivePinia } from 'pinia'
import { teardownChatbotConversation } from '@/composables/useChatbotConversation'
import { getOrganisation } from '@/services/api/organisations.api'
import {
  serviceChangePassword,
  serviceChangeTemporaryPassword,
  serviceConfirmCodeSetNewPassword,
  serviceGetUserToken,
  serviceIsLoggedIn,
  serviceLogin,
  serviceLogout,
  serviceRequestPasswordResetCode,
} from '@/services/cognito'
import { UserRoleId } from '@/types/authorisation/role-id.enum'
import { UserRoleName } from '@/types/authorisation/role-name.enum'
import { GUID_DEFAULT } from '@/utils/constants/guid'
import { HEADER_ORG_ID } from '@/utils/constants/headers'
import { ChatbotSessionId } from '@/utils/constants/session-storage'
import { localeToDateFnsLocale } from '@/utils/locale/date-fns'
import { userRoleNameLocalised } from '@/utils/user/user-role-name'

const orgsWithForecasting = [
  '3ed73cce-3403-4ad9-685d-08dcab247929',
  '8a883f82-c0bc-47c8-3420-08ddada74e30',
  'cb0b280a-3c64-419d-d623-08dd76ccc676',
  '0b029821-14f9-49ed-0ea7-08de2cbed0eb',
  '7d3b9b24-0f75-4e3d-d88d-08de8e63b230',
  'ce32adaf-f3fd-4553-2fbb-08de1adc74d3',
  'ea571b89-9e1a-49be-df3c-08de6af63f01',
]

const orgsWithSizing = [
  '3ed73cce-3403-4ad9-685d-08dcab247929',
  '8a883f82-c0bc-47c8-3420-08ddada74e30',
  'cb0b280a-3c64-419d-d623-08dd76ccc676',
  '0b029821-14f9-49ed-0ea7-08de2cbed0eb',
  '7d3b9b24-0f75-4e3d-d88d-08de8e63b230',
  'ce32adaf-f3fd-4553-2fbb-08de1adc74d3',
  'ea571b89-9e1a-49be-df3c-08de6af63f01',
]

const chatbotEnabledOrgId = '51f0c353-c674-4376-7e54-08dee9753bdf'

interface UserStore {
  locale: string
  loginRoleId?: UserRoleId
  organisationId: string
  organisationName: string
  partnerId?: string
  user: User
}

export const useUserStore = defineStore('user', {
  state: (): UserStore => ({
    locale: 'en-GB',
    loginRoleId: undefined,
    organisationId: '',
    organisationName: '',
    partnerId: undefined,
    user: {} as User,
  }),

  getters: {
    hasWritePermission(): boolean {
      return !this.isRole(UserRoleId.OrganisationUser) && !this.isRole(UserRoleId.SiteUser) && !this.isRole(UserRoleId.SiteFuelDataContributor)
    },

    isFuelDataContributor(): boolean {
      return this.isRole(UserRoleId.FuelDataContributor)
    },

    isOrganisationAdmin(): boolean {
      return this.isRole(UserRoleId.OrganisationAdmin)
    },

    isOrganisationAdminOrUser(): boolean {
      return this.isRole(UserRoleId.OrganisationAdmin) || this.isRole(UserRoleId.OrganisationUser)
    },

    isPartnerAdmin(): boolean {
      return this.isRole(UserRoleId.PartnerAdmin)
    },

    isPartnerTechnician(): boolean {
      return this.isRole(UserRoleId.PartnerTechnician)
    },

    isRole() {
      return (role: UserRoleId): boolean => this.roleId === role
    },

    isSiteFuelDataContributor(): boolean {
      return this.isRole(UserRoleId.SiteFuelDataContributor)
    },

    isSiteUser(): boolean {
      return this.isRole(UserRoleId.SiteUser)
    },

    isSysAdmin(): boolean {
      return this.isRole(UserRoleId.SysAdmin)
    },

    profileName({ user }): string {
      return user && user.firstName && user.lastName ? `${user.firstName.trim()} ${user.lastName.trim()}` : ''
    },

    roleId({ user }): UserRoleId {
      // Get it from the /users/current
      if (user && user.roleId) {
        return user.roleId
      }
      // Fallback to the role id from login
      else if (this.loginRoleId) {
        return this.loginRoleId as UserRoleId
      }

      // Default is a default guid.
      return GUID_DEFAULT as UserRoleId
    },

    roleName(): string {
      return userRoleNameLocalised(this.roleId)
    },

    getDefaultOrganisation({ user }): OrganisationSimple {
      if (user && user.organisations && Array.isArray(user.organisations) && user.organisations.length > 0 && user.organisations[0]) {
        return user.organisations[0]
      }

      return {} as OrganisationSimple
    },

    localeDate(): Locale {
      return localeToDateFnsLocale(this.locale)
    },

    isRoyalPowerEnergyOrganisation(): boolean {
      return (
        this.partnerId === '6e056f4e-1ee4-439f-4d87-08dd1b4c0dd6' // Royal Power Energy Partner ID - Production
        || this.partnerId === '08db4aff-082b-4f42-84d3-a30e701965b5' // Staging Organisation > Staging Partner Test
      )
    },

    isSunKingOrganisation(): boolean {
      return (
        this.organisationId === '08dbffbe-0f2c-41af-8f57-6c7bfefe2942' // Sun King Organisation ID - Production
        || this.organisationId === '08db46fb-9e09-430e-8e3d-3616f58302b9' // Riddler Organisation ID - Staging Test
      )
    },

    shortTermForecastEnabled(): boolean {
      return import.meta.env.MODE !== 'production'
    },

    forecastEnabled(): boolean {
      return import.meta.env.MODE !== 'production' || (import.meta.env.MODE === 'production' && orgsWithForecasting.includes(this.organisationId))
    },

    sizingEnabled(): boolean {
      return import.meta.env.VITE_FF_SIZING === 'true' || orgsWithSizing.includes(this.organisationId)
    },

    chatbotEnabled(): boolean {
      return import.meta.env.MODE === 'development'
        || import.meta.env.MODE === 'staging'
        || (import.meta.env.MODE === 'production' && this.organisationId === chatbotEnabledOrgId)
    },

    systemEditorEnabled(): boolean {
      return import.meta.env.VITE_FF_SYSTEM_EDITOR === 'true'
        || import.meta.env.MODE === 'development'
        || import.meta.env.MODE === 'staging'
    },

    userEmail(): string {
      return this.user.email || ''
    },

    userLocale(): string {
      return this.locale
    },
  },

  actions: {
    async userChangePassword(passwordOld: string, passwordNew: string) {
      return await serviceChangePassword(passwordOld, passwordNew)
    },

    async userPasswordChangeTemporary(password: string) {
      return await serviceChangeTemporaryPassword(password)
    },

    isLoggedIn(): boolean {
      return serviceIsLoggedIn()
    },

    async getUserToken(): Promise<string> {
      return await serviceGetUserToken()
    },

    storesReset() {
      const activePinia = getActivePinia()
      if (activePinia) {
        Object.entries(activePinia.state.value).forEach(([storeName, state]) => {
          const storeDefinition = defineStore(storeName, state)
          const store = storeDefinition(activePinia)
          store.$reset()
        })
      }
    },

    async userLogOut(reason?: string) {
      // Cleared before the stores are reset: `storesReset` re-runs each store's
      // `state()`, which would otherwise restore the previous user's chatbot
      // session from session storage.
      teardownChatbotConversation()
      sessionStorage.removeItem(ChatbotSessionId)

      this.storesReset()
      await serviceLogout(this.user.email)
      this.router.push(reason ? { name: 'auth-login', query: { reason } } : { name: 'auth-login' })
    },

    async userLogin(email: string, password: string): Promise<any> {
      const response = (await serviceLogin(email, password)) as any

      if (response.result instanceof CognitoUserSession) {
        const session = response.result as CognitoUserSession

        try {
          const userRole = session.getIdToken().decodePayload().userRole

          if (!userRole) {
            throw new Error('User has no role defined')
          }

          this.setUserRoleFromRoleName(userRole)
        }
        catch (error) {
          await this.userLogOut()
          throw new Error((error as Error).message || 'Unable to get user session')
        }
      }

      return response
    },

    async userRequestPasswordResetCode(email: string) {
      return await serviceRequestPasswordResetCode(email)
    },

    async userResetPasswordChange(email: string, verificationCode: string, passwordNew: string) {
      return await serviceConfirmCodeSetNewPassword(email, verificationCode, passwordNew)
    },

    switchOrganisation(organisationId: string): boolean {
      const selectedOrganisation = this.user.organisations?.find(org => org.id === organisationId)

      if (selectedOrganisation !== undefined) {
        this.setOrganisationId(organisationId as string)
        this.setOrganisationName(selectedOrganisation?.name as string)
        return true
      }

      return false
    },

    setOrganisationId(organisationId: string) {
      this.organisationId = organisationId
      localStorage.setItem(HEADER_ORG_ID, organisationId)
    },

    setOrganisationName(organisationName: string) {
      this.organisationName = organisationName
    },

    setPartnerId(partnerId?: string) {
      this.partnerId = partnerId
    },

    setLocale(locale: string) {
      this.locale = locale
    },

    /**
     * Get's the stored org-id from local storage and switch to that organisation if found in users list of organisations.
     * Otherwise returns false.
     * @returns {boolean} `true` if successful otherwise `false`.
     */
    restoreOrganisationFromLocalStorage(): boolean {
      const orgIdLocalStorage = localStorage.getItem(HEADER_ORG_ID)

      if (orgIdLocalStorage === null) {
        return false
      }

      return this.switchOrganisation(orgIdLocalStorage)
    },

    restoreSelectedOrganisation() {
      const success = this.restoreOrganisationFromLocalStorage()

      if (!success) {
        const { id, name } = this.getDefaultOrganisation

        if (id && name) {
          this.setOrganisationId(id)
          this.setOrganisationName(name)
        }
      }
    },

    async setUser(user: User) {
      if (user.expired) {
        this.userLogOut('expired')
      }

      this.user = user

      if (user.partnerId) {
        this.setPartnerId(user.partnerId)
        this.restoreSelectedOrganisation()
        return
      }

      if (!user.partnerId && user.organisations && user.organisations.length === 1 && user.organisations[0]) {
        const { id, name } = user.organisations[0]

        if (id) {
          const organisation = await getOrganisation(id)

          if (organisation && organisation.partnerId) {
            this.setPartnerId(organisation.partnerId)
          }

          this.setOrganisationId(id)
        }

        if (name) {
          this.setOrganisationName(name)
        }
      }
    },

    setUserRoleFromRoleName(roleName: string) {
      switch (roleName) {
        case UserRoleName.SysAdmin:
          this.loginRoleId = UserRoleId.SysAdmin
          break
        case UserRoleName.PartnerAdmin:
          this.loginRoleId = UserRoleId.PartnerAdmin
          break
        case UserRoleName.PartnerTechnician:
          this.loginRoleId = UserRoleId.PartnerTechnician
          break
        case UserRoleName.OrganisationAdmin:
          this.loginRoleId = UserRoleId.OrganisationAdmin
          break
        case UserRoleName.OrganisationUser:
          this.loginRoleId = UserRoleId.OrganisationUser
          break
        case UserRoleName.SiteFuelDataContributor:
          this.loginRoleId = UserRoleId.SiteFuelDataContributor
          break
        case UserRoleName.SiteUser:
          this.loginRoleId = UserRoleId.SiteUser
          break
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
